import React from 'react';
import { motion, Variants } from 'framer-motion';
import { Heart, Sparkles, Home } from 'lucide-react';

interface IdealResidentsSectionProps {
  data?: string[];
}

// --- Animation Variants (using standard Framer Motion) ---
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20, scale: 0.9 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4 } }
};

export const IdealResidentsSection: React.FC<IdealResidentsSectionProps> = ({
  data
}) => {
  if (!data || data.length === 0) return null;

  return (
    <div className="max-w-7xl mx-auto px-4">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.5 }}
        // Explicit warm, high-contrast gradient background
        className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-pink-600 to-red-500 p-8 md:p-12 text-white shadow-2xl"
        whileHover={{ scale: 1.005 }}
        transition={{ duration: 0.3 }}
      >
        {/* Decorative Home icon */}
        <motion.div
          className="absolute -top-10 -right-10 opacity-10"
          animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
          transition={{ duration: 5, repeat: Infinity }}
        >
          <Home size={150} />
        </motion.div>

        {/* Decorative Sparkles */}
        <motion.div
          className="absolute bottom-8 left-8 opacity-20 text-yellow-300"
          animate={{ y: [0, -10, 0], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Sparkles size={60} />
        </motion.div>

        <div className="relative z-10">
          {/* Header */}
          <div className="mb-10 flex items-center gap-4">
            <Heart size={36} className="text-yellow-300" />
            <h2 className="text-4xl font-extrabold md:text-5xl leading-tight">
              Your Perfect Match
            </h2>
          </div>

          <p className="mb-8 text-xl font-light italic opacity-90 max-w-4xl">
            This area’s character and facilities are best suited for:
          </p>

          {/* Persona Tags */}
          <motion.div
            variants={containerVariants}
            className="flex flex-wrap gap-4"
          >
            {data.map((persona, i) => (
              <motion.span
                key={i}
                variants={itemVariants}
                whileHover={{ scale: 1.05, y: -4 }}
                // FIXED: Responsive rounding and sizing for mobile/desktop.
                // Mobile: rounded-3xl, px-4 py-2, text-base
                // Desktop: md:rounded-full, md:px-6 md:py-3, md:text-lg
                className="inline-block rounded-3xl md:rounded-full border-2 border-white/50 bg-white/10 px-4 py-2 text-base md:px-6 md:py-3 md:text-lg font-semibold shadow-md transition-all duration-200 backdrop-blur-sm cursor-pointer"
              >
                {persona}
              </motion.span>
            ))}
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};
